/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import {getMode} from '@dvrd/dvr-controls';

const prodMode = (): boolean => getMode() === 'prod';

export const setGaUserId = (id: string | null) => {
    if (prodMode() && window.gtag) {
        if (id) window.gtag('set', {user_id: id});
        else window.gtag('set', {user_id: ''});
    }
};

export const sendGaPageView = (path?: string) => {
    if (prodMode() && window.gtag) {
        const trackingId = getTrackingID();
        if (trackingId) {
            const pathConfig: {page_path?: string; page_location: string; page_title: string; send_to: string} = {
                page_location: window.location.href,
                page_title: document.title,
                send_to: trackingId
            };
            if (path && path.length) {
                if (!path.startsWith('/')) path = `/${path}`;
                pathConfig.page_path = path;
            }
            window.gtag('event', 'page_view', pathConfig);
        }
    }
};

export const sendGaEvent = (action: string, category?: string, label?: string, value?: string) => {
    const trackingID = getTrackingID();
    if (prodMode() && trackingID && window.gtag)
        window.gtag('event', action, {event_category: category, event_label: label, value, send_to: trackingID});
};

const getTrackingID = (): string | null => {
    return window.allowCookies && prodMode() ? window.settings.sendToGaID : null;
};

export function signUpEvent(method: 'Google' | 'Manual') {
    sendGaEvent('sign_up', method);
}

export function paymentInfoEvent() {
    sendGaEvent('add_payment_info');
}

// ==========================================
// PLATFORM ANALYTICS (Internal Tracking)
// ==========================================
import {trackAnalyticsEvent} from './requestSender';

// Analytics collection enabled flag (set from /user response)
let analyticsCollectionEnabled: boolean = true; // Default to true until we know otherwise

/**
 * Set whether analytics collection is enabled
 * Called when user data is fetched from the server
 */
export function setAnalyticsCollectionEnabled(enabled: boolean): void {
    analyticsCollectionEnabled = enabled;
}

/**
 * Check if analytics collection is enabled
 */
export function isAnalyticsCollectionEnabled(): boolean {
    return analyticsCollectionEnabled;
}

// Route to feature_key mapping (both English and Dutch URLs)
const ROUTE_TO_FEATURE_KEY: Record<string, string> = {
    // Settings (English)
    '/settings': 'settings',
    '/settings/api': 'settings.api',
    '/settings/product': 'settings.products',
    '/settings/invoices': 'settings.invoices',
    '/settings/emails': 'settings.emails',
    '/settings/export': 'settings.export',
    '/settings/subscription': 'settings.subscription',
    // Settings (Dutch)
    '/instellingen': 'settings',
    '/instellingen/algemeen': 'settings',
    '/instellingen/api': 'settings.api',
    '/instellingen/producten': 'settings.products',
    '/instellingen/factuur-instellingen': 'settings.invoices',
    '/instellingen/email-instellingen': 'settings.emails',
    '/instellingen/facturen-export': 'settings.export',
    '/instellingen/abonnement': 'settings.subscription',
    '/instellingen/wachtwoord': 'settings',

    // Products
    '/producten': 'products',

    // Emails
    '/emails': 'emails',
    '/email-template': 'emails.templates',
    '/template-editor': 'emails.templates',

    // Invoices (English)
    '/invoices': 'invoices.list',
    '/invoice-details': 'invoices.detail',
    '/invoice-requests': 'invoices.requests',
    '/returns': 'invoices.returns',
    '/pdf-layout': 'invoices.layout',
    // Invoices (Dutch)
    '/facturen': 'invoices.list',
    '/factuur': 'invoices.detail',
    '/factuur-verzoeken': 'invoices.requests',
    '/retouren': 'invoices.returns',
    '/factuur-template': 'invoices.layout',

    // Notifications
    '/notifications': 'notifications',
    '/meldingen': 'notifications',

    // Reviews
    '/review-analytics': 'reviews.analytics',
    '/reviews': 'reviews',
    '/reviews-export': 'reviews.export',

    // Sales
    '/sales-dashboard': 'sales.dashboard',
    '/verkoop': 'sales.dashboard',

    // VAT
    '/vat-declaration': 'vat.declaration',
    '/btw-aangifte': 'vat.declaration',

    // Search
    '/search-volumes': 'search.terms',
    '/zoektermen': 'search.terms',

    // Admin - not tracked (excluded from analytics)

    // Account
    '/account-users': 'account.users',
    '/extra-gebruikers': 'account.users'
};

/**
 * Get feature key from route path
 */
function getFeatureKeyFromRoute(pathname: string): string {
    // Try exact match first
    if (ROUTE_TO_FEATURE_KEY[pathname]) {
        return ROUTE_TO_FEATURE_KEY[pathname];
    }

    // Try prefix match (for dynamic routes)
    const pathParts = pathname.split('/').filter(Boolean);
    for (let i = pathParts.length; i > 0; i--) {
        const prefix = '/' + pathParts.slice(0, i).join('/');
        if (ROUTE_TO_FEATURE_KEY[prefix]) {
            return ROUTE_TO_FEATURE_KEY[prefix];
        }
    }

    // Default to route itself as feature key (normalized)
    return pathname.replace(/^\//, '').replace(/\//g, '.') || 'home';
}

// Debounce tracking to avoid duplicate events
let lastTrackedPath = '';
let lastTrackTime = 0;
const DEBOUNCE_MS = 500;

// Homepage buffer - skip tracking first visit to homepage (likely a redirect after login)
let isFirstPageView = true;
const HOMEPAGE_ROUTES = [
    '/overzicht',
    '/facturen/orders',
    '/facturen/aanvragen',
    '/retouren',
    '/emails',
    '/review-analytics',
    '/meldingen'
];

// Per-feature cooldown - prevents inflating numbers when user navigates back and forth
const featureCooldowns: Map<string, number> = new Map();
const FEATURE_COOLDOWN_MS = 5000; // 5 seconds cooldown per feature

/**
 * Check if the route is a "homepage" route (default landing page after login)
 */
function isHomepageRoute(pathname: string): boolean {
    return HOMEPAGE_ROUTES.some(route => pathname.startsWith(route));
}

/**
 * Mark that the user has navigated intentionally (not a redirect)
 * Call this when user explicitly navigates to homepage
 */
export function markIntentionalNavigation(): void {
    isFirstPageView = false;
}

/**
 * Track a page view event for platform analytics
 * Note: User type filtering is done on the backend
 * Note: First visit to homepage is skipped (likely a redirect after login)
 * Note: Per-feature cooldown prevents inflating numbers on rapid navigation
 * Note: Admin routes are excluded from tracking
 * Note: If analytics collection is disabled, no request is sent
 */
export function trackPlatformPageView(pathname: string): void {
    // Skip if analytics collection is disabled
    if (!analyticsCollectionEnabled) {
        return;
    }

    // Skip admin routes - not tracked in analytics
    if (pathname.startsWith('/admin')) {
        return;
    }

    // Skip first page view if it's a homepage route (redirect after login)
    if (isFirstPageView && isHomepageRoute(pathname)) {
        isFirstPageView = false;
        lastTrackedPath = pathname;
        lastTrackTime = Date.now();
        return;
    }
    isFirstPageView = false;

    const now = Date.now();
    // Basic debounce for same path
    if (pathname === lastTrackedPath && now - lastTrackTime < DEBOUNCE_MS) {
        return;
    }

    const featureKey = getFeatureKeyFromRoute(pathname);

    // Per-feature cooldown check
    const lastFeatureTime = featureCooldowns.get(featureKey);
    if (lastFeatureTime && now - lastFeatureTime < FEATURE_COOLDOWN_MS) {
        lastTrackedPath = pathname;
        lastTrackTime = now;
        return;
    }

    lastTrackedPath = pathname;
    lastTrackTime = now;
    featureCooldowns.set(featureKey, now);

    trackAnalyticsEvent({
        data: {
            event_name: 'page_view',
            feature_key: featureKey,
            route: pathname
        }
    });
}

/**
 * Track a successful action
 * Note: User type filtering is done on the backend
 * Note: If analytics collection is disabled, no request is sent
 */
export function trackPlatformAction(featureKey: string, actionKey?: string, meta?: Record<string, unknown>): void {
    if (!analyticsCollectionEnabled) {
        return;
    }
    trackAnalyticsEvent({
        data: {
            event_name: 'action_success',
            feature_key: featureKey,
            action_key: actionKey,
            meta
        }
    });
}

/**
 * Track a failed action
 * Note: User type filtering is done on the backend
 * Note: If analytics collection is disabled, no request is sent
 */
export function trackPlatformActionFailed(
    featureKey: string,
    actionKey?: string,
    meta?: Record<string, unknown>
): void {
    if (!analyticsCollectionEnabled) {
        return;
    }
    trackAnalyticsEvent({
        data: {
            event_name: 'action_failed',
            feature_key: featureKey,
            action_key: actionKey,
            meta
        }
    });
}
